import React from "react";
import clsx from "clsx";
import {
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Link,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  ITutorialDto,
  ITutorialJourneyDto,
  TutorialJourneyStatus,
  TutorialJourneyType,
} from "@growth-machine-llc/stridist-api-client";
import { ADMIN_USERS_ROUTE } from "../../../routes/routes";
import makeStyles from "@mui/styles/makeStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { green, red } from "@mui/material/colors";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import TutorialJourneysService from "../../../services/TutorialJourneysService";
import { TUTORIAL_JOURNEYS_QUERY_KEY } from "./TutorialJourneysList";

const useStyles = makeStyles((theme) => ({
  root: {},

  link: {
    textDecoration: "underline",
    color: theme.palette.text.primary,
    "&:hover": {
      textDecoration: "none",
      color: theme.palette.text.secondary,
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(2),
    },
  },

  value: {
    fontSize: 14,
    fontWeight: 500,
  },
  description: {
    maxWidth: 360,
    whiteSpace: "normal",
    wordBreak: "break-word",
    display: "block",
    cursor: "pointer",
    color: theme.palette.text.secondary
  },
}));

export interface JourneyTableRowProps {
  journey: ITutorialJourneyDto;
  onForwardClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    journey: ITutorialJourneyDto,
  ) => void;
  onDeleteClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    journey: ITutorialJourneyDto,
  ) => void;
}

export function JourneyTableRow(props: JourneyTableRowProps) {
  const { journey, onForwardClick, onDeleteClick } = props;
  const s = useStyles();
  const queryClient = useQueryClient();
  const [descOpen, setDescOpen] = React.useState(false);
  const [descDraft, setDescDraft] = React.useState<string>(journey.description || "");

  const { mutate: updateJourney, isPending: updating } = useMutation({
    mutationFn: (payload: { id: number; description: string | null }) =>
      TutorialJourneysService.updateTutorialJourney({
        id: journey.id,
        description: payload.description,
      }),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: [TUTORIAL_JOURNEYS_QUERY_KEY] });

      const previous = queryClient.getQueryData<ITutorialJourneyDto[]>([
        TUTORIAL_JOURNEYS_QUERY_KEY,
      ]);

      if (previous) {
        const next = previous.map((j) =>
          j.id === journey.id ? { ...j, description: payload.description || null } : j,
        );
        queryClient.setQueryData([TUTORIAL_JOURNEYS_QUERY_KEY], next);
      }

      return { previous };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TUTORIAL_JOURNEYS_QUERY_KEY] });
    },
    onSuccess: () => {
      setDescOpen(false);
    },
  });

  return (
    <TableRow>
      <TableCell>
        <Typography sx={{ mb: 4 }} className={s.value}>{journey.title}</Typography>
        <Typography
          className={s.description}
          onClick={() => {
            setDescDraft(journey.description || "");
            setDescOpen(true);
          }}
        >
          {journey.description || "-"}
        </Typography>
      </TableCell>

      <TableCell>{TutorialJourneyType[journey.type]}</TableCell>

      <TableCell align="center">{journey.stepCount}</TableCell>

      <TableCell align="center">
        {journey.createdBy ? (
          <Link
            href={`${ADMIN_USERS_ROUTE}/${journey.createdBy}`}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(s.link)}
          >
            <Typography className={clsx(s.value)} component="span">
              {`${journey.createdBy}`}
            </Typography>
            <ArrowOutwardIcon fontSize="small" style={{ marginLeft: 4 }} />
          </Link>
        ) : (
          "-"
        )}
      </TableCell>

      <TableCell align="center">
        <Typography variant="caption">
          <b>Created:</b>{" "}
          {journey.created ? journey.created.format("MMM D, YYYY") : "-"}
        </Typography>
        <br />
        <Typography variant="caption">
          <b>Updated:</b>{" "}
          {journey.lastModified
            ? journey.lastModified.format("MMM D, YYYY")
            : "-"}
        </Typography>
      </TableCell>

      <TableCell align="center">
        {journey.lastModifiedBy ? (
          <Link
            href={`${ADMIN_USERS_ROUTE}/${journey.lastModifiedBy}`}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(s.link)}
          >
            <Typography className={clsx(s.value)} component="span">
              {`${journey.lastModifiedBy}`}
            </Typography>
            <ArrowOutwardIcon fontSize="small" style={{ marginLeft: 4 }} />
          </Link>
        ) : (
          "-"
        )}
      </TableCell>

      <TableCell align="center">
        <Typography
          component="span"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FiberManualRecordIcon
            fontSize="small"
            style={{
              color:
                journey.status === TutorialJourneyStatus.PUBLISHED
                  ? green[500]
                  : red[500],
              marginRight: 4,
            }}
          />
          {journey.status}
        </Typography>
      </TableCell>

      <TableCell align="center">
        <IconButton onClick={(e) => onForwardClick(e, journey)} size="large">
          <ArrowForwardRounded />
        </IconButton>
        <IconButton onClick={(e) => onDeleteClick(e, journey)}>
          <DeleteForeverIcon />
        </IconButton>
      </TableCell>
      <Dialog open={descOpen} onClose={() => setDescOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit description</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            minRows={3}
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDescOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={updating}
            onClick={() =>
              updateJourney({ id: journey.id, description: descDraft.trim() ? descDraft.trim() : null })
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
}
